import { Bell, MapPinned, RefreshCw } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { bytes, decryptMetadata } from "@tether/crypto";
import { decodeRecord, splitMetaObject, type MetadataRecord } from "@tether/device-sdk";
import { useSession } from "../App.js";
import { api, apiBytes } from "../lib/api.js";
import { loadGroupKey, openSealed } from "../lib/keys.js";

type Row = MetadataRecord & { deviceId: string };

function isoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The map + events view for one vault. Used both at `/vaults/:vaultId/timeline` and, with a vault
 *  switcher wrapped around it, at the top-level `/timeline`. */
export function TimelineView({ vaultId }: { vaultId: string }) {
  const { me } = useSession();
  const [from, setFrom] = useState(isoLocal(new Date(Date.now() - 24 * 3600_000)));
  const [to, setTo] = useState(isoLocal(new Date(Date.now() + 3600_000)));
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const mapDiv = useRef<HTMLDivElement | null>(null);

  const mine = me?.vaults.find((v) => v.id === vaultId);

  useEffect(() => {
    if (!mapDiv.current || mapRef.current) return;
    const map = L.map(mapDiv.current).setView([20, 0], 2);
    // OSM's own tile.openstreetmap.org is volunteer-run and 403s apps that don't follow its usage
    // policy (osm.wiki/Blocked); CARTO's basemaps now require a signed-up API key. Esri's tile
    // service needs neither and is the standard no-key fallback for direct Leaflet use.
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, attribution: "Tiles &copy; Esri" },
    ).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
  }, []);

  const load = useCallback(async () => {
    if (!me || !mine) return;
    setBusy(true);
    setErr(null);
    try {
      const kp = loadGroupKey(me.user.id);
      if (!kp)
        throw new Error(
          "Your decryption key is missing in this browser. Restore it from your backup in Settings to read location history.",
        );
      if (!mine.sealedMetadataSecret)
        throw new Error(
          mine.role === "confidant"
            ? "The owner has not confirmed you yet."
            : "No metadata secret for this vault.",
        );
      const msk = openSealed(kp, mine.sealedMetadataSecret, "msk");
      const mpk = bytes.fromBase64Url(mine.metadataPublicKey);
      const f = new Date(from).getTime();
      const t = new Date(to).getTime();
      const { objects } = await api<{ objects: { deviceId: string; key: string }[] }>(
        `/v1/vaults/${vaultId}/meta?from=${f}&to=${t}`,
      );
      const out: Row[] = [];
      for (const o of objects) {
        const data = await apiBytes(
          `/v1/vaults/${vaultId}/object?key=${encodeURIComponent(o.key)}`,
        );
        for (const sealed of splitMetaObject(data)) {
          try {
            out.push({
              ...decodeRecord(decryptMetadata(msk, mpk, vaultId, o.deviceId, sealed)),
              deviceId: o.deviceId,
            });
          } catch {
            /* skip records we cannot open */
          }
        }
      }
      msk.fill(0);
      out.sort((a, b) => a.ts - b.ts);
      setRows(out.filter((r) => r.ts >= f && r.ts <= t));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [me, mine, vaultId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const pts: [number, number][] = [];
    for (const r of rows)
      if (r.t === "location") for (const p of r.points) pts.push([p.lat, p.lon]);
    if (pts.length === 0) return;
    L.polyline(pts, { color: "#1f6feb", weight: 4 }).addTo(layer);
    L.circleMarker(pts[0]!, { radius: 6, color: "#1a7f37" }).bindTooltip("start").addTo(layer);
    L.circleMarker(pts[pts.length - 1]!, { radius: 7, color: "#c0392b" })
      .bindTooltip("latest")
      .addTo(layer);
    for (const r of rows) {
      if (r.t === "event" && (r.kind === "sos" || r.kind === "trigger")) {
        const near = nearest(rows, r.ts);
        if (near)
          L.marker([near.lat, near.lon])
            .bindTooltip(`${r.kind} · ${new Date(r.ts).toLocaleTimeString()}`)
            .addTo(layer);
      }
    }
    map.fitBounds(L.latLngBounds(pts), { padding: [24, 24] });
  }, [rows]);

  const events = rows.filter((r) => r.t === "event");
  const locs = rows.filter((r) => r.t === "location");
  return (
    <div>
      <h2 style={{ margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 }}>
        <MapPinned size={20} className="icon" />
        {mine?.name ?? "Vault"} · timeline
      </h2>
      <div className="card">
        <div className="row">
          <div>
            <label>From</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label>To</label>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div style={{ alignSelf: "end" }}>
            <button
              className="primary"
              onClick={load}
              disabled={busy}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <RefreshCw size={14} className={busy ? "spin" : ""} />
              {busy ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
        {err && <p className="error">{err}</p>}
        <p className="muted">
          {locs.reduce((n, r) => n + (r.t === "location" ? r.points.length : 0), 0)} location points
          · {events.length} events. Decrypted in your browser; the gateway cannot read this.
        </p>
      </div>
      <div className="map" ref={mapDiv} />
      <div className="card" style={{ marginTop: 16 }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Bell size={18} className="icon" />
          Events
        </h2>
        {events.length === 0 && <p className="muted">No events in this range.</p>}
        <ul className="list-plain">
          {events.map((r, i) =>
            r.t === "event" ? (
              <li key={i}>
                <strong>{r.kind}</strong> · {new Date(r.ts).toLocaleString()}{" "}
                <span className="muted">· {r.deviceId}</span>
                {r.data && <span className="muted"> · {JSON.stringify(r.data)}</span>}
              </li>
            ) : null,
          )}
        </ul>
      </div>
    </div>
  );
}

/** Route-bound wrapper for `/vaults/:vaultId/timeline`. */
export function Timeline() {
  const { vaultId = "" } = useParams();
  return <TimelineView vaultId={vaultId} />;
}

function nearest(rows: Row[], ts: number): { lat: number; lon: number } | null {
  let best: { lat: number; lon: number; d: number } | null = null;
  for (const r of rows) {
    if (r.t !== "location") continue;
    for (const p of r.points) {
      const d = Math.abs(p.ts - ts);
      if (!best || d < best.d) best = { lat: p.lat, lon: p.lon, d };
    }
  }
  return best;
}
